/**
 * Cross-tab session notifications.
 *
 * Without these, a tab keeps acting on a session that another tab has already logged out or
 * superseded. Before per-tab accounts that was invisible, because every tab shared one session
 * pointer. Now it is the difference between a tab noticing it has been signed out and a tab silently
 * issuing requests as somebody else.
 *
 * BroadcastChannel where available, with the `storage` event as a fallback — it fires in other tabs
 * on any localStorage write, which is enough to carry a message through a scratch key.
 */

import { readDeviceValue, writeDeviceValue } from "./SessionStorage";
import { deviceKey, getActiveAppId } from "./StorageKeys";

export const SESSION_EVENTS = {
  /** A session signed in. detail: { sessionId } */
  SESSION_ADDED: "session-added",
  /** One session logged out or was evicted. detail: { sessionId } */
  SESSION_REMOVED: "session-removed",
  /** Every session on this origin was logged out. detail: {} */
  LOGOUT_ALL: "logout-all",
};

const CHANNEL_NAME = "bayun:session:v1";
const FALLBACK_KEY = "sessionEvent";

let channel;
const listeners = new Set();

const openChannel = () => {
  if (channel !== undefined) {
    return channel;
  }
  try {
    channel = typeof BroadcastChannel === "function" ? new BroadcastChannel(CHANNEL_NAME) : null;
  } catch (error) {
    channel = null;
  }
  if (channel) {
    channel.onmessage = (event) => deliver(event.data);
  }
  return channel;
};

const deliver = (message) => {
  if (!message || !message.type) {
    return;
  }
  // Two Bayun apps on one origin share both transports: the channel name is not per app, and the
  // storage event fires for any key. Without this filter one app's "logout-all" — which names no
  // session, so no listener can tell it is foreign — would sign the user out of the other app.
  if ((message.appId || "") !== getActiveAppId()) {
    return;
  }
  listeners.forEach((listener) => {
    try {
      listener(message);
    } catch (error) {
      // One bad listener must not stop the others from being told.
      console.error("Session event listener failed", error);
    }
  });
};

let fallbackAttached = false;

const attachFallback = () => {
  if (fallbackAttached || typeof window === "undefined" || !window.addEventListener) {
    return;
  }
  fallbackAttached = true;
  window.addEventListener("storage", (event) => {
    if (!event.newValue || !event.key) {
      return;
    }
    // Exact key, not a suffix: another app on this origin writes its own, under its own appId.
    // deliver() also checks the payload, so this is the cheap first pass rather than the guarantee.
    let ourKey;
    try {
      ourKey = deviceKey(FALLBACK_KEY);
    } catch (error) {
      return;
    }
    if (event.key !== ourKey) {
      return;
    }
    try {
      deliver(JSON.parse(event.newValue));
    } catch (error) {
      // Not one of ours, or truncated.
    }
  });
};

/**
 * Registers a listener for session events raised by *other* tabs.
 *
 * @returns {Function} unsubscribe
 */
export const onSessionEvent = (listener) => {
  openChannel();
  attachFallback();
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Tells other tabs about a session change. The publishing tab does not receive its own event —
 * it already knows, and re-entering its own handler is how logout loops start.
 */
export const publishSessionEvent = (type, detail = {}) => {
  // appId travels with the message so a receiver can drop another app's events; see deliver().
  const message = { type, detail, appId: getActiveAppId(), at: Date.now() };
  const active = openChannel();
  if (active) {
    try {
      active.postMessage(message);
      return;
    } catch (error) {
      // Fall through to the storage fallback.
    }
  }
  attachFallback();
  try {
    // The value must differ every time or the storage event does not fire; `at` supplies that.
    writeDeviceValue(FALLBACK_KEY, JSON.stringify(message));
  } catch (error) {
    // Nothing more to try: other tabs will find out on their next request instead.
  }
};

/** Test seam. */
export const lastPublishedEvent = () => {
  try {
    const raw = readDeviceValue(FALLBACK_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};
